"use client";

import { useState } from "react";
import { ArrowUpRight, Target, ChartBar as BarChart3, CircleCheck as CheckCircle2, Sparkles, ShieldCheck, ChevronRight, Clock } from 'lucide-react';
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
export default function Crmleadmanagement() {
  const [activeTab, setActiveTab] = useState<number>(0);
  const {
    ref: heroRef,
    isVisible: heroVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const features = [{
    id: "instant-response",
    title: "Instant Response Engine",
    subtitle: "Sub-60 Second Engagement",
    icon: Clock,
    stat: "< 60s",
    statLabel: "Average Lead Touchpoint",
    description: "Automatically reach out to inbound leads via SMS and email within seconds of submission. Stop losing high-intent prospects to faster-responding competitors.",
    bullets: ["Automated 2-way AI conversational response", "Multi-channel routing (SMS, Email, Web Chat)", "Instant notification alerts for high-value leads"],
    previewMetric: "94% Speed-to-Lead Rate"
  }, {
    id: "lead-scoring",
    title: "AI Lead Scoring",
    subtitle: "Smart Pipeline Prioritization",
    icon: Target,
    stat: "3.8x",
    statLabel: "Sales Qualified Lift",
    description: "Automatically analyze prospect metadata, search intent, and business size to score leads before handing them to your sales team.",
    bullets: ["Intent analysis based on GEO & AI search queries", "Automated CRM tag assignment and stage movement", "Custom rule sets for local service businesses"],
    previewMetric: "100% Qualified Routing"
  }, {
    id: "unified-crm",
    title: "Unified Pipeline Hub",
    subtitle: "Centralized Deal Tracking",
    icon: BarChart3,
    stat: "100%",
    statLabel: "Lead Visibility Across Channels",
    description: "Consolidate calls, form fills, booking links, and message threads into a single visual Kanban board configured specifically for your service workflows.",
    bullets: ["Seamless integration with custom and popular CRMs", "Real-time pipeline value calculation", "Automated stage triggers and task assignments"],
    previewMetric: "$48.5k Active Pipeline"
  }, {
    id: "review-booster",
    title: "Automated Review Engine",
    subtitle: "Reputation & Review Loop",
    icon: ShieldCheck,
    stat: "4.9",
    statLabel: "Average Client Reputation Score",
    description: "Trigger post-service SMS review requests at the exact right moment to consistently generate 5-star Google reviews and bolster your local search signal.",
    bullets: ["Triggered upon project completion in your CRM", "Automated follow-ups for unfulfilled requests", "Direct routing to Google Business & platform profiles"],
    previewMetric: "+28 Reviews / Month"
  }];
  const currentFeature = features[activeTab];
  return <section data-section-id="5200" id="crmleadmanagement" className="bg-background text-foreground py-24 md:py-36 px-6 md:px-12 overflow-hidden border-b border-border/40">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={heroRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-16 transition-all duration-700 ${heroVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-12 flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <span className="font-mono text-xs md:text-sm tracking-widest uppercase text-accent-deep-red font-semibold flex items-center gap-2">
                <span className="size-2 rounded-lg bg-accent-deep-red inline-block animate-pulse" />
                02 / CRM & LEAD MANAGEMENT
              </span>
              <div className="h-px w-16 bg-accent-deep-red/60" />
            </div>

            <h1 className="font-sans font-black text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight uppercase">
              AUTOMATE YOUR PIPELINE. <br />
              CONVERT <span className="italic text-accent-deep-red font-serif">EVERY</span> LEAD.
            </h1>

            <p className="max-w-3xl text-lg md:text-xl text-muted-foreground leading-relaxed font-default mt-2">
              Transform high-intent traffic from AI search engine results into loyal clients with automated speed-to-lead response, intelligent scoring, and seamless CRM tracking built specifically for local service businesses.
            </p>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start mb-16">
          {}
          <div className="lg:col-span-5 flex flex-col gap-6">
            <div className="flex flex-col gap-3">
              <span className="font-mono text-xs text-muted-foreground uppercase tracking-widest">
                SELECT A CORE CAPABILITY
              </span>
              <div className="flex flex-col gap-2">
                {features.map((feature, idx) => {
                const IconComponent = feature.icon;
                const isActive = activeTab === idx;
                return <Button key={feature.id} data-index={idx} variant="ghost" onClick={() => setActiveTab(idx)} className={`w-full h-auto text-left p-4 md:p-5 rounded-lg border transition-all duration-300 flex items-center justify-between group whitespace-normal ${isActive ? "bg-card border-accent-deep-red shadow-md text-foreground hover:bg-card hover:text-foreground" : "bg-card/40 border-border/60 hover:border-border hover:bg-card/70 text-muted-foreground"}`}>
                      <div className="flex items-center gap-3 md:gap-4 text-left">
                        <div className={`p-2.5 rounded-lg transition-colors ${isActive ? "bg-accent-deep-red text-white" : "bg-muted text-muted-foreground group-hover:text-foreground"}`}>
                          <IconComponent className="size-5" />
                        </div>
                        <div>
                          <div className="font-sans font-bold text-sm md:text-base uppercase tracking-tight text-foreground">
                            {feature.title}
                          </div>
                          <div className="font-mono text-xs text-muted-foreground">
                            {feature.subtitle}
                          </div>
                        </div>
                      </div>
                      <ChevronRight className={`size-5 transition-transform duration-300 shrink-0 ${isActive ? "text-accent-deep-red translate-x-1" : "text-muted-foreground/40 group-hover:translate-x-0.5"}`} />
                    </Button>;
              })}
              </div>
            </div>

            {}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-4 border-t border-border/50">
              <Button asChild className="bg-accent-deep-red hover:bg-accent-deep-red/90 text-white font-semibold rounded-lg px-6 py-3.5 h-auto shadow-md">
                <Link to="/ContactPage" className="flex items-center justify-center gap-2">
                  Book a Strategy Call
                  <ArrowUpRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" className="border-border hover:bg-accent text-foreground font-medium rounded-lg px-6 py-3.5 h-auto">
                <Link to="/VisibilityCheckupPage" className="flex items-center justify-center">
                  Free AI Checkup
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <Card className="bg-card/90 backdrop-blur-xl border border-border/80 rounded-lg p-6 md:p-10 shadow-lg flex flex-col justify-between h-full min-h-[480px] relative overflow-hidden">
              {}
              <div className="absolute -top-24 -right-24 size-72 bg-accent-deep-red/10 rounded-lg blur-3xl pointer-events-none" />

              <div>
                {}
                <div className="flex flex-wrap items-center justify-between gap-4 pb-6 border-b border-border/60">
                  <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-accent-deep-red bg-accent-deep-red/10 px-3 py-1.5 rounded-lg border border-accent-deep-red/20">
                    <Sparkles className="size-3.5" />
                    Feature Detail 0{activeTab + 1}
                  </div>
                  <div className="text-right">
                    <div className="font-mono font-bold text-2xl md:text-3xl text-foreground">
                      {currentFeature.stat}
                    </div>
                    <div className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                      {currentFeature.statLabel}
                    </div>
                  </div>
                </div>

                {}
                <div className="mt-6 mb-8">
                  <h3 className="font-sans font-extrabold text-2xl md:text-3xl uppercase tracking-tight mb-3">
                    {currentFeature.title}
                  </h3>
                  <p className="font-default text-base md:text-lg leading-relaxed text-muted-foreground">
                    {currentFeature.description}
                  </p>
                </div>

                {}
                <div className="space-y-3 mb-8">
                  <div className="font-mono text-xs uppercase text-muted-foreground tracking-widest mb-2">
                    KEY CAPABILITIES
                  </div>
                  {currentFeature.bullets.map((bullet, bIdx) => <div key={bIdx} data-index={bIdx} className="flex items-start gap-3">
                      <div className="mt-1 p-1 rounded-lg bg-accent-deep-red/20 text-accent-deep-red shrink-0">
                        <CheckCircle2 className="size-4" />
                      </div>
                      <span className="font-default text-sm md:text-base text-foreground font-medium">
                        {bullet}
                      </span>
                    </div>)}
                </div>
              </div>

              {}
              <div className="mt-auto pt-6 border-t border-border/60 bg-muted/30 -mx-6 md:-mx-10 -mb-6 md:-mb-10 p-6 md:p-8 rounded-b-lg flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="size-3 rounded-lg bg-emerald-500 animate-ping" />
                  <div>
                    <div className="font-mono text-xs text-muted-foreground uppercase">
                      SYSTEM STATUS
                    </div>
                    <div className="font-mono text-sm font-semibold text-foreground">
                      {currentFeature.previewMetric}
                    </div>
                  </div>
                </div>

                <Button asChild variant="ghost" size="sm" className="font-mono text-xs tracking-widest uppercase text-accent-deep-red hover:text-accent-deep-red hover:bg-accent-deep-red/10 p-2 h-auto">
                  <Link to="/ContactPage" className="flex items-center gap-1.5">
                    CONFIGURE CRM SYSTEM
                    <ArrowUpRight className="size-4" />
                  </Link>
                </Button>
              </div>
            </Card>
          </div>
        </div>

        {}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 pt-8 border-t border-border/40">
          {[{
          label: "Lead Response Time",
          value: "< 60 Sec",
          sub: "Automated SMS & Email"
        }, {
          label: "CRM Integration",
          value: "Native CRM Integration",
          sub: "Full Pipeline Sync"
        }, {
          label: "Conversion Lift",
          value: "+38%",
          sub: "From Qualified Traffic"
        }, {
          label: "Setup Time",
          value: "72 Hours",
          sub: "Done-for-You System"
        }].map((metric, i) => <div key={i} data-index={i} className="bg-card/50 border border-border/40 p-4 md:p-6 rounded-lg">
              <div className="font-mono text-xl md:text-2xl font-bold text-accent-deep-red mb-1">
                {metric.value}
              </div>
              <div className="font-sans font-bold text-xs md:text-sm text-foreground uppercase">
                {metric.label}
              </div>
              <div className="font-mono text-[11px] text-muted-foreground mt-0.5">
                {metric.sub}
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}